<template>
  <div class="aroma-scanner">
    <aroma-secondary-nav title="绑定设备" fallback="AromaReportList" />
    <main class="aroma-scanner__content">
      <h1>扫描设备二维码</h1>
      <p>将二维码放入框内，识别后确认设备编号</p>
      <div class="aroma-scanner__camera">
        <video ref="video" muted playsinline></video>
        <span aria-hidden="true"></span>
        <div v-if="submitting" class="aroma-scanner__camera-error"><van-loading color="#fff">正在绑定设备</van-loading></div>
        <div v-else-if="cameraError" class="aroma-scanner__camera-error">{{ cameraError }}</div>
      </div>
      <van-button type="primary" block :disabled="scanning || submitting" :loading="submitting" loading-text="正在绑定" @click="startScanner">{{ scanning ? '正在识别' : '重新扫码' }}</van-button>
      <button class="aroma-scanner__manual" type="button" :disabled="submitting" @click="openManual">手动输入设备编号</button>
    </main>

    <van-popup v-model="manualVisible" round position="bottom" safe-area-inset-bottom>
      <section class="aroma-scanner__sheet">
        <h2>输入设备编号</h2>
        <van-field v-model="manualSn" input-class="ios-no-zoom" placeholder="请输入设备编号" clearable />
        <van-button type="primary" block @click="confirmManual">下一步</van-button>
      </section>
    </van-popup>
  </div>
</template>

<script>
import jsQR from 'jsqr'
import API from '@/api'
import AromaSecondaryNav from '@/components/aroma-report/AromaSecondaryNav.vue'
import { extractSniFromQrText, resolveDeviceAction } from '@/utils/aroma-binding'
import {
  canUseAromaBarcodeDetector,
  getAromaBarcodeDetectorOptions,
  getAromaBrowserCameraConstraints,
  getAromaQrCanvasSize,
  getAromaScanRegion
} from '@/utils/aroma-browser-scan'
import { applySniLoginResult, getActiveSniSession, normalizeSniSn } from '@/utils/sni-session'

export default {
  name: 'AromaDeviceScanner',
  components: { AromaSecondaryNav },
  data () {
    return {
      scanning: false,
      cameraError: '',
      manualVisible: false,
      manualSn: '',
      scannerRequestId: 0,
      deviceRequestId: 0,
      confirming: false,
      submitting: false,
      browserStream: null,
      browserScanTimer: null,
      browserScanCanvas: null,
      browserScanActive: false,
      barcodeDetector: null
    }
  },
  computed: {
    session () { return getActiveSniSession('') },
  },
  mounted () {
    this.startScanner()
    document.addEventListener('visibilitychange', this.onVisibilityChange)
  },
  beforeDestroy () {
    this.deviceRequestId += 1
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
    this.stopScanner()
  },
  methods: {
    async startScanner () {
      if (this.submitting || this.confirming) return
      this.stopScanner()
      const requestId = ++this.scannerRequestId
      this.cameraError = ''

      this.scanning = true
      await this.startBrowserScanner(requestId)
    },
    async startBrowserScanner (requestId) {
      this.scanning = true
      if (canUseAromaBarcodeDetector(typeof window === 'undefined' ? undefined : window.BarcodeDetector)) {
        try {
          await this.startBarcodeDetectorScanner(requestId)
          return
        } catch (error) {
          if (requestId !== this.scannerRequestId || this._isDestroyed) return
          this.stopBrowserResources()
        }
      }
      try {
        await this.startJsQrScanner(requestId)
      } catch (error) {
        if (requestId !== this.scannerRequestId || this._isDestroyed) return
        this.stopBrowserResources()
        this.cameraError = '无法使用摄像头，请手动输入设备编号'
        this.scanning = false
      }
    },
    async startBarcodeDetectorScanner (requestId) {
      const detector = new window.BarcodeDetector(getAromaBarcodeDetectorOptions())
      await this.startBrowserStream(requestId)
      const canvas = this.createBrowserScanCanvas()
      this.browserScanCanvas = canvas
      this.browserScanActive = true
      this.barcodeDetector = detector
      const detect = async () => {
        if (!this.browserScanActive || requestId !== this.scannerRequestId || this._isDestroyed) return
        try {
          this.drawBrowserScanRegion(canvas)
          const results = await detector.detect(canvas)
          if (results && results[0] && results[0].rawValue) {
            const sn = extractSniFromQrText(results[0].rawValue)
            if (sn) {
              this.browserScanActive = false
              this.scanning = false
              await this.confirmSn(sn)
              return
            }
          }
        } catch (error) {
          if (requestId !== this.scannerRequestId || this._isDestroyed) return
          this.stopBrowserResources()
          try {
            await this.startJsQrScanner(requestId)
          } catch (fallbackError) {
            if (requestId !== this.scannerRequestId || this._isDestroyed) return
            this.cameraError = '无法使用摄像头，请手动输入设备编号'
            this.scanning = false
          }
          return
        }
        this.browserScanTimer = setTimeout(detect, 100)
      }
      await detect()
    },
    async startJsQrScanner (requestId) {
      await this.startBrowserStream(requestId)
      try {
        const canvas = this.browserScanCanvas || this.createBrowserScanCanvas()
        this.browserScanCanvas = canvas
        this.browserScanActive = true
        const decode = () => {
          if (!this.browserScanActive || requestId !== this.scannerRequestId || this._isDestroyed) return
          this.drawBrowserScanRegion(canvas)
          const context = canvas.getContext('2d', { willReadFrequently: true })
          const image = context.getImageData(0, 0, canvas.width, canvas.height)
          const result = jsQR(image.data, image.width, image.height, { inversionAttempts: 'dontInvert' })
          const sn = extractSniFromQrText(result && result.data)
          if (sn) {
            this.browserScanActive = false
            this.scanning = false
            this.confirmSn(sn)
            return
          }
          this.browserScanTimer = setTimeout(decode, 60)
        }
        decode()
      } catch (error) {
        this.stopBrowserResources()
        this.cameraError = '无法使用摄像头，请手动输入设备编号'
        this.scanning = false
      }
    },
    async startBrowserStream (requestId) {
      const stream = await navigator.mediaDevices.getUserMedia(getAromaBrowserCameraConstraints())
      if (requestId !== this.scannerRequestId || this._isDestroyed) {
        stream.getTracks().forEach(track => track.stop())
        return false
      }
      this.browserStream = stream
      const video = this.$refs.video
      video.srcObject = stream
      if (video.readyState < 2) {
        await new Promise(resolve => {
          const timeout = setTimeout(resolve, 1500)
          video.addEventListener('loadedmetadata', () => {
            clearTimeout(timeout)
            resolve()
          }, { once: true })
        })
      }
      await video.play()
      return true
    },
    createBrowserScanCanvas () {
      const video = this.$refs.video
      const width = video.videoWidth || 640
      const height = video.videoHeight || 480
      const region = getAromaScanRegion(width, height)
      const size = getAromaQrCanvasSize(region.sw, region.sh)
      const canvas = document.createElement('canvas')
      canvas.width = size.width
      canvas.height = size.height
      canvas._aromaRegion = region
      return canvas
    },
    drawBrowserScanRegion (canvas) {
      const video = this.$refs.video
      const region = canvas._aromaRegion
      canvas.getContext('2d', { willReadFrequently: true }).drawImage(
        video,
        region.sx,
        region.sy,
        region.sw,
        region.sh,
        0,
        0,
        canvas.width,
        canvas.height
      )
    },
    stopBrowserResources () {
      this.browserScanActive = false
      if (this.browserScanTimer) clearTimeout(this.browserScanTimer)
      this.browserScanTimer = null
      if (this.browserStream) this.browserStream.getTracks().forEach(track => track.stop())
      this.browserStream = null
      if (this.$refs.video) this.$refs.video.srcObject = null
      this.browserScanCanvas = null
      this.barcodeDetector = null
    },
    stopScanner () {
      this.scannerRequestId += 1
      this.stopBrowserResources()
      this.scanning = false
    },
    onVisibilityChange () {
      if (document.hidden) this.stopScanner()
    },
    openManual () {
      if (this.submitting || this.confirming) return
      this.stopScanner()
      this.manualVisible = true
    },
    confirmManual () {
      if (this.submitting || this.confirming) return
      const sn = normalizeSniSn(this.manualSn)
      if (!sn) {
        this.$toast('请输入设备编号')
        return
      }
      this.manualVisible = false
      this.confirmSn(sn)
    },
    async confirmSn (sn) {
      if (this.submitting || this.confirming) return
      this.stopScanner()
      this.confirming = true
      try {
        await this.$dialog.confirm({
          title: '确认设备',
          message: `设备编号：${sn}`,
          confirmButtonText: '确认绑定',
          cancelButtonText: '重新识别'
        })
      } catch (error) {
        this.confirming = false
        this.startScanner()
        return
      }
      this.confirming = false
      if (!this.session || !this.session.token) {
        this.$router.replace({ name: 'AromaReportLogin', query: { sn } })
        return
      }
      await this.submitDevice(sn)
    },
    async submitDevice (sn) {
      if (this.submitting) return
      this.submitting = true
      const requestId = ++this.deviceRequestId
      try {
        const action = resolveDeviceAction(this.session, sn)
        const result = action === 'switch'
          ? await API.sniSwitchDevice({ sn }, this.session.token)
          : await API.sniBindDevice({ sn }, this.session.token)
        if (requestId !== this.deviceRequestId || this._isDestroyed) return
        const session = applySniLoginResult(result)
        this.$router.replace({ name: 'AromaReportList', query: { sn: session.deviceSn || sn } })
      } catch (error) {
        if (requestId !== this.deviceRequestId || this._isDestroyed) return
        this.$toast(error?.message || error)
      } finally {
        if (requestId === this.deviceRequestId && !this._isDestroyed) this.submitting = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
.aroma-scanner { min-height: 100%; overflow-x: hidden; background: #f5f6f8; }
.aroma-scanner__content { max-width: 520px; margin: 0 auto; padding: 28px 20px calc(32px + env(safe-area-inset-bottom)); text-align: center; box-sizing: border-box; }
h1 { margin: 0; color: #242b3d; font-size: 24px; line-height: 34px; }
p { margin: 8px 0 22px; color: #8a92a2; font-size: 14px; }
.aroma-scanner__camera { position: relative; width: 100%; aspect-ratio: 1; overflow: hidden; border-radius: 8px; background: #111; }
.aroma-scanner__camera video { width: 100%; height: 100%; object-fit: cover; }
.aroma-scanner__camera > span { position: absolute; inset: 15%; border: 2px solid #fff; border-radius: 4px; box-shadow: 0 0 0 999px rgba(0, 0, 0, .32); }
.aroma-scanner__camera-error { position: absolute; inset: 0; display: grid; place-items: center; padding: 32px; color: #fff; background: #272a31; font-size: 15px; }
.aroma-scanner__content .van-button { height: 48px; margin-top: 20px; border-color: #596afd; border-radius: 6px; background: #596afd; }
.aroma-scanner__manual { min-height: 44px; margin-top: 12px; padding: 0 16px; border: 0; color: #596afd; background: transparent; font-size: 15px; }
.aroma-scanner__manual:disabled { color: #aeb4c2; }
.aroma-scanner__sheet { padding: 24px 20px calc(24px + env(safe-area-inset-bottom)); }
.aroma-scanner__sheet h2 { margin: 0 0 18px; color: #242b3d; font-size: 19px; }
.aroma-scanner__sheet .van-field { margin-bottom: 18px; border: 1px solid #e4e6ec; border-radius: 6px; }
.aroma-scanner__sheet ::v-deep .van-field__control { font-size: 16px; }
.aroma-scanner__sheet .van-button { height: 46px; border-color: #596afd; border-radius: 6px; background: #596afd; }
</style>
